import { useState, useCallback } from "react";
import styles from "./ContactForm.module.css";
import axios from 'axios';
import { AppConstants } from "../../../AppConstants";
import Loader from "../../Loader/Loader";
import Toast from "../../Toast/Toast";

const ContactForm = () => {
    const formlink = process.env.REACT_APP_FORM_LINK;
    const [enteredName, setEnteredName] = useState("");
    const [enteredEmail, setEnteredEmail] = useState("");
    const [enteredMessage, setEnteredMessage] = useState("");
    const [honeypot, setHoneypot] = useState("");
    const [touchedfields, setTouchedfields] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toast, setToast] = useState(null);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        if (name === "name") {
            setEnteredName(value);
        } else if (name === "email") {
            setEnteredEmail(value);
        } else if (name === "message") {
            setEnteredMessage(value);
        }
    };

    const isFieldInvalid = (fieldname) => {
        if (fieldname === "name") {
            return enteredName.trim() === "" && touchedfields.includes(fieldname);
        }
        if (fieldname === "email") {
            return (!enteredEmail.trim() || !emailRegex.test(enteredEmail.trim())) && touchedfields.includes(fieldname);
        }
        if (fieldname === "message") {
            return enteredMessage.trim() === "" && touchedfields.includes(fieldname);
        }
        return false;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setTouchedfields(['name', 'email', 'message']);

        if (enteredName.trim() === "" || !emailRegex.test(enteredEmail.trim()) || enteredMessage.trim() === "") {
            return;
        }

        // Honeypot check — bots will fill this hidden field
        if (honeypot) {
            return;
        }

        const formData = new FormData();
        formData.append(AppConstants.ContactForm.FormKeys.Name, enteredName);
        formData.append(AppConstants.ContactForm.FormKeys.Email, enteredEmail);
        formData.append(AppConstants.ContactForm.FormKeys.Message, enteredMessage);

        setIsSubmitting(true);

        try {
            await axios.post(formlink, formData);
            setEnteredName("");
            setEnteredEmail("");
            setEnteredMessage("");
            setTouchedfields([]);
            setToast({
                message: AppConstants.ContactForm.ThankYouForSubmitting,
                type: "success"
            });
        } catch (error) {
            console.error('Error sending data to backend:', error);
            setEnteredName("");
            setEnteredEmail("");
            setEnteredMessage("");
            setTouchedfields([]);
            setToast({
                message: `Error submitting the form. You can directly email at ${AppConstants.SocialLinks.Email}`,
                type: "error"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputBlur = (event) => {
        const { name } = event.target;
        if (!touchedfields.includes(name)) {
            setTouchedfields((prevField) => [...prevField, name]);
        }
    };

    const handleToastClose = useCallback(() => {
        setToast(null);
    }, []);

    if (isSubmitting) {
        return <Loader message="Sending your message..." />;
    }

    return (
        <>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={handleToastClose}
                />
            )}
            <h3 className={styles.heading}>{AppConstants.ContactForm.SendAMessage}</h3>
            <form onSubmit={handleSubmit}>
                {/* Honeypot field — hidden from real users, catches bots */}
                <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                />
                <div className={`${styles["form-control"]} ${isFieldInvalid('name') && styles.invalid}`}>
                    <input
                        type="text"
                        name="name"
                        placeholder={AppConstants.ContactForm.Placeholders.Name}
                        value={enteredName}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        autoComplete="off"
                    />
                    {
                        isFieldInvalid('name') && <p className={styles.error} style={{ marginTop: "0" }}>{AppConstants.ContactForm.Validation.NameRequired}</p>
                    }
                </div>
                <div className={`${styles["form-control"]} ${isFieldInvalid('email') && styles.invalid}`}>
                    <input
                        type="email"
                        name="email"
                        placeholder={AppConstants.ContactForm.Placeholders.Email}
                        value={enteredEmail}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        autoComplete="off"
                    />
                    {
                        isFieldInvalid('email') && <p className={styles.error} style={{ marginTop: "0" }}>{AppConstants.ContactForm.Validation.EmailInvalid}</p>
                    }
                </div>
                <div className={`${styles["form-control"]} ${isFieldInvalid('message') && styles.invalid}`}>
                    <textarea
                        name="message"
                        placeholder={AppConstants.ContactForm.Placeholders.Message}
                        className={styles.textarea}
                        value={enteredMessage}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        autoComplete="off"
                    ></textarea>
                    {
                        isFieldInvalid('message') && <p className={styles.error} style={{ marginTop: "0" }}>{AppConstants.ContactForm.Validation.MessageRequired}</p>
                    }
                </div>
                <button className={styles.button} disabled={isSubmitting}>{AppConstants.ContactForm.ButtonText}</button>
            </form>
        </>
    );
};
export default ContactForm;
